/**
 * useCommandPaletteShortcut — install a global ⌘K / Ctrl+K listener
 * that toggles the CSK command palette.
 *
 * The hook intentionally lives next to the palette so consumers wire
 * one import for the surface and one import for the keystroke
 * binding. The default chord is `mod+k` (⌘K on macOS, Ctrl+K on
 * everything else). Callers can opt-out per-key by overriding the
 * `keys` argument or pass `enabled={false}` to skip the listener.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect } from "react";

export interface CommandPaletteShortcutOptions {
  /** Whether to toggle the palette open / closed on the chord. */
  readonly enabled?: boolean;
  /**
   * Lower-cased keys that should fire the chord. Defaults to `["k"]`
   * which matches the conventional `⌘K` / `Ctrl+K` palette shortcut.
   */
  readonly keys?: readonly string[];
}

const DEFAULT_KEYS: readonly string[] = ["k"];

function isModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey;
}

/**
 * Register a window-level keydown listener that toggles `setOpen`
 * whenever the user presses `⌘K` (macOS) or `Ctrl+K` (other OSes).
 */
export function useCommandPaletteShortcut(
  setOpen: (updater: (current: boolean) => boolean) => void,
  options: CommandPaletteShortcutOptions = {},
): void {
  const enabled = options.enabled !== false;
  const keys = options.keys ?? DEFAULT_KEYS;
  // Depend on the chord *content*, not the array identity: a caller passing
  // an inline `keys={["k"]}` literal re-creates the array every render, which
  // would otherwise tear down and re-add the window listener on each render.
  // `\n` is never a `KeyboardEvent.key` value, so it is a collision-free join.
  const keySignature = keys.join("\n");

  useEffect(() => {
    if (!enabled) return undefined;
    const chordKeys = new Set(keySignature.split("\n"));
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isModifier(event)) return;
      if (!chordKeys.has(event.key.toLowerCase())) return;
      event.preventDefault();
      setOpen((current) => !current);
    };
    window.addEventListener("keydown", onKeyDown);
    return (): void => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled, keySignature, setOpen]);
}
